import { useEffect, useState } from "react";
import { UserPlus, Settings, Mail, Shield, Palette, Loader2 } from "lucide-react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import Badge from "../../components/ui/badge";
import { ProfessionalColorPicker } from "./components/ProfessionalColorPicker";
import { useLanguage } from "../../contexts/LanguageContext";
import { employeesLabels } from "./labels";
import type { Professional } from "../../types";
import { navigate } from "../../lib/router";

import { NewEmployeeModal } from "./components/NewEmployeeModal";
import { EditEmployeeModal } from "./components/EditEmployeeModal";

export function EmployeesPage() {
  const { lang } = useLanguage();
  const t = employeesLabels[lang].page;
  const [items, setItems] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<{ id: number; is_admin: boolean } | null>(null);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [editingProfessional, setEditingProfessional] = useState<Professional | null>(null);

  const load = async () => {
    const [res, meRes] = await Promise.all([
      api.listProfessionals(),
      api.me(),
    ]);
    if (res.ok) setItems(res.data);
    if (meRes.ok)
      setMe({ id: meRes.data.id, is_admin: Boolean(meRes.data.is_admin) });
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 p-6">
      {/* Header Area */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-neutral-800 tracking-tight">{t.title}</h1>
          <p className="text-xs font-medium text-neutral-400 uppercase tracking-widest">
            {items.length} {t.title.toLowerCase()}
          </p>
        </div>
        {me?.is_admin && (
          <Button 
            onClick={() => setIsNewModalOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 px-6 rounded-2xl transition-all active:scale-95 flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            {t.new}
          </Button>
        )}
      </div>

      <NewEmployeeModal 
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        onSuccess={load}
      />

      <EditEmployeeModal
        professional={editingProfessional}
        isOpen={!!editingProfessional}
        onClose={() => setEditingProfessional(null)}
        onSuccess={load}
      />

      {/* Professional List Card */}
      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-20 flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest">{t.loading}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-20 text-center">
            <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest">{t.empty}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-neutral-50">
                  <TableHead className="h-14 px-6 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                    <div className="flex items-center gap-2">
                      <Settings className="w-3 h-3" />
                      {t.table.name}
                    </div>
                  </TableHead>
                  <TableHead className="h-14 px-6 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                    <div className="flex items-center gap-2">
                      <Palette className="w-3 h-3" />
                      {t.table.color}
                    </div>
                  </TableHead>
                  <TableHead className="h-14 px-6 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3" />
                      {t.table.email}
                    </div>
                  </TableHead>
                  <TableHead className="h-14 px-6 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                    <div className="flex items-center gap-2">
                      <Shield className="w-3 h-3" />
                      {t.table.role}
                    </div>
                  </TableHead>
                  <TableHead className="h-14 px-6 text-right text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                    {t.table.actions}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((prof) => (
                  <TableRow key={prof.id} className="group hover:bg-neutral-50/50 transition-colors border-b border-neutral-50 last:border-0">
                    <TableCell className="px-6 py-4 font-bold text-neutral-800 text-sm">
                      {prof.name}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <ProfessionalColorPicker
                        professional={prof}
                        onColorChange={load}
                      />
                    </TableCell>
                    <TableCell className="px-6 py-4 text-neutral-500 text-sm">
                      {prof.email}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <Badge 
                        variant={prof.is_admin ? "success" : "secondary"}
                        className="rounded-lg px-2 py-0.5 font-bold uppercase tracking-widest text-[9px]"
                      >
                        {prof.is_admin ? t.roles.admin : t.roles.user}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      {(me?.is_admin || me?.id === prof.id) && (
                        <Button
                          variant="ghost"
                          onClick={() => setEditingProfessional(prof)}
                          className="h-9 px-4 font-bold text-[10px] uppercase tracking-widest text-emerald-600 hover:text-white hover:bg-emerald-600 rounded-xl transition-all"
                        >
                          {lang === 'pt' ? 'Editar' : 'Edit'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
